import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { currentCohortPathSelector } from '../Auth/selectors';
import { courseMembershipSelector } from '../CourseMembership/selectors';
import {
  selectCurrentCoursePath,
  currentCourseSelector,
} from '../Courses/selectors';
import { enrolmentsSelector } from '../Enrolments/selectors';

export const cohortsSelector = (state) => state.Cohorts;

export const currentCohortSelector = createSelector(
  cohortsSelector,
  currentCohortPathSelector,
  (cohorts, currentCohort) => {
    return cohorts.get(currentCohort, null);
  }
);

export const currentCohortScheduleData = createSelector(
  currentCohortSelector,
  (cohortData) => {
    if (cohortData) {
      return {
        classAccessTz: cohortData.get('classAccessTz', null),
        startDate: cohortData.get('startDate', null),
        endDate: cohortData.get('endDate', null),
        isScheduled: cohortData.get('isScheduled', false),
        isOpen: cohortData.get('isOpen', true),
        isSelfPaced: cohortData.get('isSelfPaced', false),
      };
    }
    return null;
  }
);

export const selectCurrentCohortEnrolmentSettings = createSelector(
  currentCohortSelector,
  (cohortData) => {
    if (cohortData) {
      return {
        enrolmentType: cohortData.getIn(
          ['enrolmentSettings', 'enrolmentType'],
          null
        ),
        enrolmentCloseDate: cohortData.getIn(
          ['enrolmentSettings', 'enrolmentCloseDate'],
          null
        ),
      };
    }
    return null;
  }
);

export const currentCourseCohortsByPathSelector = createSelector(
  cohortsSelector,
  selectCurrentCoursePath,
  (cohorts, currentCourse) => {
    return cohorts.filter(
      (cohort) => cohort.get('coursePath') === currentCourse
    );
  }
);

export const currentCourseCohortsSelector = createSelector(
  currentCourseCohortsByPathSelector,
  (cohorts) => cohorts.toList()
);

export const cohortsByIdSelector = createSelector(
  cohortsSelector,
  (cohorts) => {
    return cohorts.reduce(
      (current, value) => current.set(value.get('id'), value),
      Immutable.Map()
    );
  }
);

export const currentUserEnrolledCohortsSelector = createSelector(
  enrolmentsSelector,
  currentCourseCohortsByPathSelector,
  (enrolments, courseCohorts) => {
    let enrolledCohorts = Immutable.Map();

    enrolments.forEach((enrolment) => {
      enrolledCohorts = enrolledCohorts.set(
        enrolment.get('cohortPath'),
        enrolment.get('timestamp')
      );
    });

    // sort by enrolment timestamps to match existing behaviour
    return courseCohorts
      .filter((cohort) => enrolledCohorts.has(cohort.get('path')))
      .toList()
      .sortBy((cohort) => enrolledCohorts.get(cohort.get('path')))
      .reverse();
  }
);

export const selectCohortsByCoursePath = createSelector(
  cohortsSelector,
  (cohorts) => {
    return cohorts.reduce(
      (byCoursePath, cohort) =>
        byCoursePath.setIn(
          [cohort.get('coursePath'), cohort.get('path')],
          cohort
        ),
      Immutable.Map()
    );
  }
);

export const selectCurrentCourseDefaultCohort = createSelector(
  currentCourseSelector,
  cohortsSelector,
  (course, cohorts) => {
    const defaultCohortPath = course ? course.get('defaultCohort') : null;
    if (defaultCohortPath) {
      const defaultCohort = cohorts.get(defaultCohortPath);
      if (defaultCohort) {
        return defaultCohort.toJS();
      }
    }
    return null;
  }
);

export const makeSelectCohortByPath = (cohortPath) =>
  createSelector(cohortsSelector, (cohorts) =>
    cohorts.has(cohortPath) ? cohorts.get(cohortPath).toJS() : null
  );

export const selectMakePublicCohortsForCourse = createSelector(
  cohortsSelector,
  (cohorts) => (coursePath) =>
    cohorts.filter(
      (cohort) =>
        cohort.get('coursePath') === coursePath &&
        cohort.getIn(['enrolmentSettings', 'enrolmentType']) === 'open' &&
        (!cohort.get('endDate') ||
          new Date(cohort.get('endDate')) >= Date.now()) &&
        (!cohort.getIn(['enrolmentSettings', 'enrolmentCloseDate']) ||
          new Date(cohort.getIn(['enrolmentSettings', 'enrolmentCloseDate'])) >=
            Date.now())
    )
);

export const selectPublicCohortsForCurrentCourse = createSelector(
  selectMakePublicCohortsForCourse,
  selectCurrentCoursePath,
  (makeCohorts, currentCourse) => makeCohorts(currentCourse)
);

export const selectActiveCohortByCoursePath = (coursePath) =>
  createSelector(
    [courseMembershipSelector, cohortsSelector],
    (courseMembershipList, cohorts) => {
      const cohortPath = courseMembershipList.getIn([
        coursePath,
        'activeCohortPath',
      ]);
      return cohortPath ? cohorts.get(cohortPath) : null;
    }
  );
